import { motion } from "framer-motion";
import { Eye, Disc, Navigation2, Search, Lightbulb, CircleDot, Frame, Settings, Wind } from "lucide-react";

const controlFamilies = [
  { icon: Lightbulb, name: "Éclairage", points: 15, color: "bg-yellow-500", defectRate: "33%", details: "Phares, feux arrière, clignotants, feux de jour" },
  { icon: Disc, name: "Freinage", points: 20, color: "bg-red-500", defectRate: "22%", details: "Plaquettes, disques, liquide de frein, frein à main" },
  { icon: CircleDot, name: "Pneumatiques", points: 12, color: "bg-gray-700", defectRate: "25%", details: "Usure, pression, craquelures, hernies" },
  { icon: Settings, name: "Suspension", points: 18, color: "bg-blue-600", defectRate: "15%", details: "Amortisseurs, ressorts, silent blocs, rotules" },
  { icon: Navigation2, name: "Direction", points: 15, color: "bg-indigo-500", defectRate: "8%", details: "Jeu de direction, crémaillère, biellettes" },
  { icon: Wind, name: "Pollution", points: 18, color: "bg-green-600", defectRate: "12%", details: "Filtre à air, catalyseur, sonde lambda, opacité" },
  { icon: Search, name: "Équipements", points: 20, color: "bg-orange-500", defectRate: "10%", details: "Essuie-glaces, ceintures, rétroviseurs, klaxon" },
  { icon: Frame, name: "Structure", points: 10, color: "bg-purple-500", defectRate: "5%", details: "Corrosion, pare-brise, carrosserie" },
  { icon: Eye, name: "Identification", points: 5, color: "bg-teal-500", defectRate: "2%", details: "Plaque, numéro de série, carte grise" },
];

export const ControlPointsInfographic = () => {
  const totalPoints = controlFamilies.reduce((sum, f) => sum + f.points, 0);

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl font-bold text-autosur-navy mb-4">
            Les {totalPoints} points de contrôle expliqués
          </h2>
          <p className="font-body text-muted-foreground max-w-2xl mx-auto">
            Le contrôle technique vérifie {totalPoints} points réglementaires, regroupés en 9 familles. 
            Voici le détail de chaque famille et le taux de défauts associé.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
          {controlFamilies.map((family, index) => (
            <motion.div
              key={family.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 ${family.color} rounded-lg flex items-center justify-center`}>
                  <family.icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-bold text-autosur-navy text-sm">{family.name}</h3>
                  <p className="font-body text-muted-foreground text-xs">{family.points} points</p>
                </div>
                <div className="text-right">
                  <span className="font-heading font-bold text-autosur-red text-sm">{family.defectRate}</span>
                  <p className="font-body text-muted-foreground text-[10px]">défauts</p>
                </div>
              </div>
              <div className="w-full bg-gray-100 rounded-full h-1.5 mb-2">
                <div className={`${family.color} rounded-full h-1.5`} style={{ width: `${(family.points / 20) * 100}%` }} />
              </div>
              <p className="font-body text-muted-foreground text-xs">{family.details}</p>
            </motion.div>
          ))}
        </div>

        {/* Total */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-5xl mx-auto mt-8 bg-autosur-navy rounded-2xl p-6 text-white text-center"
        >
          <p className="font-heading text-3xl font-bold">{totalPoints} points contrôlés</p>
          <p className="font-body text-white/70 text-sm mt-1">
            en 30-45 minutes • Résultat immédiat • Procès-verbal officiel
          </p>
        </motion.div>
      </div>
    </section>
  );
};
